import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  PackageCheck, Droplets, Wind, Zap, Users, ClipboardList, Layers, AlertTriangle,
} from 'lucide-react';

const n = (v, d = 0) => Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: d });
const cop = (v) => `$${n(v, 0)}`;

const Stat = ({ label, value, hint }) => (
  <div className="rounded-lg border border-border bg-muted/30 p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-lg font-semibold mt-0.5">{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
  </div>
);

const Section = ({ icon, title, children }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
    {children}
  </div>
);

const ConsolidadoDetailModal = ({ summary, open, onClose }) => {
  if (!summary) return null;
  const rd = summary.reportData || {};
  const k = summary.kpis || {};
  const rt = rd.receptionTotals || {};
  const ut = rd.utilityTypeTotals || {};
  const wh = rd.workedHours || {};
  const al = rd.alistamientos || {};
  const productos = Array.isArray(rd.productConsumption) ? rd.productConsumption : [];
  const programas = Array.isArray(rd.programasLavado) ? rd.programasLavado : [];

  const causas = [
    ['Arrastre', rt.arrastre], ['Cloro', rt.cloro], ['Grasa', rt.grasa], ['Tintas', rt.tintas],
    ['Óxido', rt.oxido], ['Clorhexidina', rt.clorhexidina], ['Pegante', rt.pegante],
    ['Azul de metileno', rt.azul_de_metileno], ['Otro', rt.otro],
  ].filter(([, v]) => Number(v || 0) > 0);

  const servicios = [
    ['Agua', ut.agua, <Droplets key="a" className="h-3.5 w-3.5 text-sky-500" />],
    ['Agua caliente', ut.aguaCaliente, <Droplets key="ac" className="h-3.5 w-3.5 text-orange-500" />],
    ['Gas', ut.gas, <Wind key="g" className="h-3.5 w-3.5 text-amber-500" />],
    ['Electricidad', ut.Electricidad, <Zap key="e" className="h-3.5 w-3.5 text-yellow-500" />],
  ];

  const topProductos = [...productos].sort((a, b) => Number(b?.totalCosto || 0) - Number(a?.totalCosto || 0));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Consolidado {summary.periodLabel} — {summary.lavanderia?.nombre}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* KPIs principales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Costo por Kg" value={cop(k.costoKiloTotal)} hint={`${n(k.kilosProcesados)} kg procesados`} />
            <Stat label="% Rechazo" value={`${Number(k.porcentajeRechazo || 0).toFixed(1)}%`} />
            <Stat label="Kg / hora autorizada" value={Number(k.kgPorHoraAutorizada || 0).toFixed(2)} />
            <Stat label="Consumo insumos (L)" value={n(k.totalConsumoProductos, 2)} />
          </div>

          {/* Desglose costo por kg */}
          <Section icon={<PackageCheck className="h-4 w-4 text-violet-500" />} title="Costo por kilogramo">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Productos" value={cop(k.costoProductosPorKg)} hint={`Total ${cop(k.costoProductos)}`} />
              <Stat label="Servicios" value={cop(k.costoServiciosPorKg)} hint={`Total ${cop(k.costoServicios)}`} />
              <Stat label="Mano de obra" value={cop(k.costoManoObraPorKg)} hint={`Total ${cop(k.costoManoObra)}`} />
            </div>
          </Section>

          {/* Recepción */}
          <Section icon={<ClipboardList className="h-4 w-4 text-emerald-500" />} title="Recepción">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Kg procesados" value={n(rt.kilosProcesados)} />
              <Stat label="Kg rechazo" value={n(rt.kilosRechazo, 2)} />
              <Stat label="Pendientes" value={n(rt.pendientes)} />
              <Stat label="Lotes" value={n(rt.conteo)} />
            </div>
            {causas.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {causas.map(([label, v]) => (
                  <span key={label} className="inline-flex items-center gap-1 text-xs rounded-full bg-red-500/10 text-red-600 dark:text-red-400 px-2.5 py-1">
                    <AlertTriangle className="h-3 w-3" /> {label}: {n(v, 2)} kg
                  </span>
                ))}
              </div>
            )}
          </Section>

          {/* Lavado y Secado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Section icon={<Layers className="h-4 w-4 text-indigo-500" />} title="Ciclos de lavado">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Kg lavados" value={n(rd.washingTotals?.kilosLavados)} />
                <Stat label="Ciclos" value={n(rd.washingTotals?.ciclos)} />
                <Stat label="Ciclos abortados" value={n(rd.washingAborted?.ciclos)} />
                <Stat label="Kg abortados" value={n(rd.washingAborted?.kilos)} />
              </div>
              <MachineTable rows={rd.washingByMachine} kilosKey="kilosLavados" />
            </Section>
            <Section icon={<Wind className="h-4 w-4 text-cyan-500" />} title="Ciclos de secado">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Kg secados" value={n(rd.dryingTotals?.kilosSecados)} />
                <Stat label="Ciclos" value={n(rd.dryingTotals?.ciclos)} />
                <Stat label="Ciclos abortados" value={n(rd.dryingAborted?.ciclos)} />
                <Stat label="Kg abortados" value={n(rd.dryingAborted?.kilos)} />
              </div>
              <MachineTable rows={rd.dryingByMachine} kilosKey="kilosSecados" />
            </Section>
          </div>

          {/* Servicios públicos */}
          <Section icon={<Zap className="h-4 w-4 text-yellow-500" />} title="Servicios públicos">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-1.5 pr-4 font-medium">Servicio</th>
                    <th className="py-1.5 pr-4 font-medium text-right">Consumo (L)</th>
                    <th className="py-1.5 pr-4 font-medium text-right">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {servicios.map(([label, val, icon]) => (
                    <tr key={label} className="border-b border-border/50">
                      <td className="py-1.5 pr-4 flex items-center gap-1.5">{icon}{label}</td>
                      <td className="py-1.5 pr-4 text-right">{n(val?.consumoLitros, 2)}</td>
                      <td className="py-1.5 pr-4 text-right">{cop(val?.costos)}</td>
                    </tr>
                  ))}
                  <tr className="font-semibold">
                    <td className="py-1.5 pr-4">Total</td>
                    <td className="py-1.5 pr-4 text-right">{n(rd.utilityTotals?.consumoLitros, 2)}</td>
                    <td className="py-1.5 pr-4 text-right">{cop(rd.utilityTotals?.costoTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          {/* Mano de obra */}
          <Section icon={<Users className="h-4 w-4 text-blue-500" />} title="Mano de obra">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Horas trabajadas" value={n(wh.horasTrabajadas, 1)} />
              <Stat label="Horas autorizadas" value={n(wh.horasAutorizadas, 1)} />
              <Stat label="Costo por hora" value={cop(k.costoPorHora)} />
            </div>
          </Section>

          {/* Detergentes / insumos */}
          {topProductos.length > 0 && (
            <Section icon={<Droplets className="h-4 w-4 text-teal-500" />} title="Detergentes / insumos">
              <div className="overflow-x-auto max-h-56 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-1.5 pr-4 font-medium">Producto</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Cantidad usada</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProductos.map((p, i) => (
                      <tr key={p?.producto?.id || i} className="border-b border-border/50">
                        <td className="py-1.5 pr-4">{p?.producto?.nombre || '—'}</td>
                        <td className="py-1.5 pr-4 text-right">{n(p?.totalUsado, 2)}</td>
                        <td className="py-1.5 pr-4 text-right">{cop(p?.totalCosto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Programas de lavado */}
          {programas.length > 0 && (
            <Section icon={<Layers className="h-4 w-4 text-purple-500" />} title="Programas de lavado">
              <div className="overflow-x-auto max-h-56 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-1.5 pr-4 font-medium">Programa</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Kg</th>
                      <th className="py-1.5 pr-4 font-medium text-right">Conteo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programas.map((p, i) => (
                      <tr key={p?.nombre || i} className="border-b border-border/50">
                        <td className="py-1.5 pr-4">{p?.nombre || 'Sin programa'}</td>
                        <td className="py-1.5 pr-4 text-right">{n(p?.kilos, 2)}</td>
                        <td className="py-1.5 pr-4 text-right">{n(p?.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Alistamientos / planchado */}
          <Section icon={<ClipboardList className="h-4 w-4 text-pink-500" />} title="Alistamientos / planchado">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Stat label="Total prendas" value={n(al.totalPrendas)} hint={`${n(al.totalAlistamientos)} alistamientos`} />
              <Stat label="Lencería (prendas)" value={n(al.prendasLenceria)} hint={`${n(al.totalKilosLenceria, 2)} kg`} />
              <Stat label="Toallas (prendas)" value={n(al.prendasToallas)} hint={`${n(al.totalKilosToallas, 2)} kg`} />
            </div>
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MachineTable = ({ rows, kilosKey }) => {
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-border">
            <th className="py-1 pr-3 font-medium">Máquina</th>
            <th className="py-1 pr-3 font-medium text-right">Kg</th>
            <th className="py-1 pr-3 font-medium text-right">Ciclos</th>
            <th className="py-1 pr-3 font-medium text-right">% Kg</th>
          </tr>
        </thead>
        <tbody>
          {arr.map((m, i) => (
            <tr key={m?.id || i} className="border-b border-border/40">
              <td className="py-1 pr-3">{m?.nombre || '—'}</td>
              <td className="py-1 pr-3 text-right">{n(m?.[kilosKey], 2)}</td>
              <td className="py-1 pr-3 text-right">{n(m?.ciclos)}</td>
              <td className="py-1 pr-3 text-right">{Number(m?.pctKilos || 0).toFixed(1)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ConsolidadoDetailModal;
